/**
 * "Describe it, we fill the form": the backend turns a plain-language sentence into
 * values for a dashboard create form (users, visitor passes, schedules, sites).
 * Nothing is written; the admin reviews the real form and presses Save.
 *
 * Field names are the API body names (snake_case); ids are strings. The mappers
 * below turn them into what each antd form expects.
 */
import moment, { Moment } from 'moment';
import api from 'utils/api';
import countryList from 'config/country';
import { ICustomMap } from 'types/ICustomMap';
import { ISchedule, IScheduleTime } from 'pages/Schedules/types';
import { IVisitorPass } from 'pages/VisitorPins/types';
import { EScheduleAccessType } from 'pages/Schedules/config';

export type TFormKind = 'user' | 'visitor_pass' | 'door_unlock_schedule' | 'access_schedule' | 'site' | 'tenant';

export interface IFormFill {
    form: TFormKind;
    label: string;
    route: string;
    fields: ICustomMap;
    labels: ICustomMap<string | string[]>;
    assumptions: string[];
    missing: string[];
    summary?: string;
    /** The text was a question or a command, not a description of this record. */
    unrelated?: boolean;
    ms?: number;
}

/** What a pending chat write carries when the create is backed by a dashboard form. */
export interface IActionForm {
    kind: TFormKind;
    label: string;
    route: string;
    fields: ICustomMap;
    labels: ICustomMap<string | string[]>;
    assumptions: string[];
    missing: string[];
}

/** react-router location state used to hand a fill (or just focus) to a form page. */
export interface IAssistantLocationState {
    assistantFill?: IFormFill;
    assistantDescribe?: boolean;
}

export const DATE_TIME = 'YYYY-MM-DD HH:mm';

export const fillForm = (kind: TFormKind, text: string): Promise<{ data: IFormFill }> => api.post(`assistant/forms/${kind}/fill`, { text });

export const fillFromAction = (form: IActionForm): IFormFill => ({ ...form, form: form.kind });

const toMoment = (value: unknown, format?: string): Moment | undefined => {
    if (typeof value !== 'string' || !value) return undefined;
    const parsed = format ? moment(value, format, true) : moment(value);
    return parsed.isValid() ? parsed : undefined;
};

/** Minutes east of UTC for `tz` at the instant `utcMs` (Intl, no timezone library). */
const zoneOffsetMinutes = (utcMs: number, tz: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(utcMs));
    const get = (type: string) => Number(parts.find(p => p.type === type)?.value || 0);
    const asIfUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return Math.round((asIfUtc - utcMs) / 60000);
};

/**
 * 'YYYY-MM-DD HH:mm' written in the form's time zone -> UTC moment. The backend normalises
 * date-times to the site's zone; parsing them as browser-local showed 1 PM Pacific as
 * 11 AM to an admin on Central time (production feedback 2026-11-02). Falls back to
 * browser-local when the zone is unknown.
 */
export const zonedToMoment = (value: unknown, tz: unknown): Moment | undefined => {
    const local = toMoment(value, DATE_TIME);
    if (!local || typeof tz !== 'string' || !tz) return local;
    const text = value as string;
    const wall = Date.UTC(
        Number(text.slice(0, 4)),
        Number(text.slice(5, 7)) - 1,
        Number(text.slice(8, 10)),
        Number(text.slice(11, 13)),
        Number(text.slice(14, 16)),
    );
    try {
        let utc = wall - zoneOffsetMinutes(wall, tz) * 60000;
        const second = zoneOffsetMinutes(utc, tz); // DST edge: re-evaluate at the corrected instant
        utc = wall - second * 60000;
        return moment.utc(utc);
    } catch (e) {
        return local; // unknown zone name
    }
};

/** Add User form: one entry of the `fields` Form.List. */
export const toUserFormValues = (fill: IFormFill): ICustomMap => {
    const f = fill.fields;
    const values: ICustomMap = {};
    (['first_name', 'last_name', 'email', 'role', 'user_groups', 'unit_ids', 'tenant_name'] as const).forEach(key => {
        if (f[key] !== undefined) values[key] = f[key];
    });
    (['send_invite', 'create_tenant_directory', 'assign_bluetooth_mobile_access'] as const).forEach(key => {
        if (typeof f[key] === 'boolean') values[key] = f[key];
    });
    const activation = toMoment(f.activation_datetime, 'YYYY-MM-DD');
    const deactivation = toMoment(f.deactivation_datetime, 'YYYY-MM-DD');
    if (activation) values.activation_date = activation;
    if (deactivation) values.deactivation_date = deactivation;
    if (f.create_tenant_directory && !values.tenant_name && (f.first_name || f.last_name)) {
        values.tenant_name = `${f.first_name || ''} ${f.last_name || ''}`.trim();
    }
    return values;
};

const scheduleMap = (details: unknown): ICustomMap<IScheduleTime> | null => {
    if (!Array.isArray(details) || details.length === 0) return null;
    const map: ICustomMap<IScheduleTime> = {};
    details.forEach((day: IScheduleTime) => {
        if (day && day.weekday && Array.isArray(day.slots)) map[day.weekday] = day;
    });
    return Object.keys(map).length ? map : null;
};

/** Schedule forms (door unlock and access): the `ISchedule` shape the form already hydrates from. */
export const toSchedulePrefill = (fill: IFormFill): ISchedule => {
    const f = fill.fields;
    return {
        id_str: '',
        schedule_name: f.schedule_name || '',
        schedule_desc: f.schedule_desc || f.schedule_name || '',
        timezone: f.timezone || '',
        access_type: (f.access_type as EScheduleAccessType) || undefined,
        standard_schedule: scheduleMap(f.schedule_details),
    };
};

const labelAt = (fill: IFormFill, key: string, index: number): string => {
    const labels = fill.labels[key];
    return Array.isArray(labels) ? labels[index] || '' : '';
};

/** Visitor pass form: an `IVisitorPass`-shaped object so the existing sections hydrate from it. */
export const toVisitorPassPrefill = (fill: IFormFill): IVisitorPass => {
    const f = fill.fields;
    const start = zonedToMoment(f.start_date_time, f.timezone);
    const end = zonedToMoment(f.end_date_time, f.timezone);
    const siteId = typeof f.site_id === 'string' ? f.site_id : '';
    const doorIds: string[] = Array.isArray(f.access_point_ids) ? f.access_point_ids : [];
    const unitIds: string[] = Array.isArray(f.unit_ids) ? f.unit_ids : [];
    const prefill = {
        id_str: '',
        company_id_str: '',
        created_at: moment.utc().format(),
        created_by: '',
        updated_at: moment.utc().format(),
        status: '',
        pass_name: f.pass_name || f.visitor_name || '',
        pass_type: f.pass_type || 'Single',
        description: f.description || f.pass_name || f.visitor_name || '',
        // undefined, never '': the submit handler calls .unix() on anything truthy-or-string
        // (feedback 2026-10-20: "activateDate.unix is not a function" blocked Create)
        start_date_time: start ? start.utc().format() : undefined,
        end_date_time: end ? end.utc().format() : undefined,
        visitor_name: f.visitor_name || '',
        visitor_email: f.visitor_email || '',
        notes: f.notes,
        receive_usage_notification: false,
        site_id_str: siteId,
        site_name: typeof fill.labels.site_id === 'string' ? fill.labels.site_id : '',
        sites: siteId ? [{ id_str: siteId, name: typeof fill.labels.site_id === 'string' ? fill.labels.site_id : '' }] : [],
        access_points: doorIds.map((id, index) => ({ id_str: id, short_name: labelAt(fill, 'access_point_ids', index) })),
        units: unitIds.map((id, index) => ({ id_str: id, name: labelAt(fill, 'unit_ids', index) })),
        floors: null,
        timezone: f.timezone,
        // custom PIN when the admin asked for one; the form validates length against the workspace rules
        pin: typeof f.pin === 'string' && f.pin ? f.pin : undefined,
        access_type: f.access_type as EScheduleAccessType | undefined,
        schedule_details: scheduleMap(f.schedule_details),
        send_email: f.send_email,
    };
    return prefill as unknown as IVisitorPass;
};

/** Common short forms the model (or an admin) may use for countries in the dashboard list. */
const COUNTRY_ALIASES: Record<string, string> = {
    us: 'United States',
    usa: 'United States',
    'u.s.': 'United States',
    'u.s.a.': 'United States',
    'united states of america': 'United States',
    america: 'United States',
    uk: 'United Kingdom',
    'u.k.': 'United Kingdom',
    britain: 'United Kingdom',
    'great britain': 'United Kingdom',
    england: 'United Kingdom',
    uae: 'United Arab Emirates',
};

/** Country name from the dashboard list, matched case-insensitively with aliases; undefined if unknown. */
export const matchCountry = (value: string): string | undefined => {
    const wanted = value.trim().toLowerCase();
    if (!wanted) return undefined;
    const alias = COUNTRY_ALIASES[wanted];
    const target = (alias || wanted).toLowerCase();
    return countryList.find((name: string) => name.toLowerCase() === target);
};

/** Intercom directory entry form values. */
export const toTenantFormValues = (fill: IFormFill): ICustomMap => {
    const f = fill.fields;
    const values: ICustomMap = {};
    if (typeof f.site_id === 'string' && f.site_id) values.site_id = f.site_id;
    if (Array.isArray(f.user_ids) && f.user_ids.length) values.user_ids = f.user_ids;
    const firstPerson = Array.isArray(fill.labels.user_ids) ? fill.labels.user_ids[0] : undefined;
    if (typeof f.display_name === 'string' && f.display_name) values.display_name = f.display_name;
    else if (firstPerson) values.display_name = firstPerson.slice(0, 32);
    if (typeof f.name === 'string' && f.name) values.name = f.name;
    else if (values.display_name) values.name = values.display_name;
    return values;
};

/** Add Site form values; the country must match the dashboard's list exactly. */
export const toSiteFormValues = (fill: IFormFill): ICustomMap => {
    const f = fill.fields;
    const values: ICustomMap = {};
    (['name', 'address', 'timezone', 'phone_number'] as const).forEach(key => {
        if (typeof f[key] === 'string' && f[key]) values[key] = f[key];
    });
    if (typeof f.country === 'string' && f.country) {
        const match = matchCountry(f.country);
        if (match) values.country = match;
    }
    return values;
};

/** Plain-language example per form, shown as the describe box placeholder. */
export const FORM_EXAMPLES: Record<TFormKind, string> = {
    user: 'Priya Patel, priya@example.com, resident, Staff group, starts Monday',
    visitor_pass: 'Plumber Bob, bob@example.com, Front Door, tomorrow 9am to 1pm',
    door_unlock_schedule: 'Business hours: Monday to Friday 9am to 5pm, Front Door',
    access_schedule: 'Night shift: weekdays 10pm to 6am',
    site: 'Harbor Lofts, 120 Harbor St, Boston MA, +1 617 555 0100',
    tenant: 'Unit 4B, rings Priya Patel and Sam Lee, show as "Patel / Lee"',
};

export const FORM_ROUTES: Record<TFormKind, string> = {
    user: '/users/add/',
    visitor_pass: '/visitor-pins/add-pin/',
    door_unlock_schedule: '/schedules/door-unlock/new/',
    access_schedule: '/schedules/new/',
    site: '/sites/add/',
    tenant: '/intercom-settings/tenants/new/',
};
